// Visual das telas de entrada (login, redefinir senha, manutenção, verificação em duas etapas).
// Extraído de App.js sem alteração de comportamento.
import React from "react";
import { loadLS } from "../lib/storage";
import { hexToRgb, loadThemePref, parseThemeStyle, makeTheme } from "../lib/theme";

// ── Paleta das telas fora do app (login, reset, e-mail confirmado, manutenção, abertura) ──
// Lê o estilo salvo no navegador. "Clássico" mantém exatamente as cores antigas.
export function authPalette() {
  const ps = parseThemeStyle(loadThemePref());
  const t = makeTheme(ps.style, loadLS("sf_themeMode", "dark"), loadLS("sf_accentId", "blue"), ps.instBase, ps.instMetal);
  if (t.style === "classico") {
    return {
      themed: false, rgb: "96,165,250", rgbLt: "147,197,253",
      ac: "#60a5fa", ac2: "#3b82f6", acLight: "#93c5fd", onAc: "#060d1a",
      text: "#e8eef0", dim: "#607080", faint: "#3b5070", errText: "#fca5a5", okText: "#86efac",
      pageBg: "radial-gradient(ellipse 80% 60% at 50% 0%, #08152a 0%, #030810 60%)",
      pageBg2: "radial-gradient(circle at 20% 80%, rgba(20,40,100,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(10,28,70,0.25) 0%, transparent 50%)",
      bodyBg: "#030810", cardBg: "rgba(6,13,26,0.82)", cardBg2: "rgba(4,9,20,0.88)",
      cardShadow: "0 0 0 1px rgba(96,165,250,0.06), 0 32px 80px rgba(0,0,0,0.7), 0 0 60px rgba(96,165,250,0.05)",
      cardShadow2: "0 32px 80px rgba(0,0,0,0.8)",
      inputBg: "rgba(255,255,255,0.04)", chipBg: "rgba(255,255,255,0.03)", tickOff: "rgba(255,255,255,0.08)",
      btnBg: "linear-gradient(135deg, #3b82f6 0%, #60a5fa 50%, #93c5fd 100%)",
      fontDisplay: "'Barlow Condensed',sans-serif", fontBody: "'Outfit', sans-serif", fontLayout: "'Inter','Outfit',sans-serif", fontInter: "'Inter',sans-serif",
    };
  }
  const [r, g, b] = hexToRgb(t.ac);
  const rgb = `${r},${g},${b}`;
  const dark = t.mode === "dark";
  return {
    themed: true, rgb, rgbLt: rgb,
    ac: t.ac, ac2: t.ac, acLight: t.ac, onAc: t.onAc,
    text: t.text, dim: t.textDim, faint: t.textFaint,
    errText: dark ? "#fca5a5" : "#b91c1c", okText: dark ? "#86efac" : "#15803d",
    pageBg: `radial-gradient(ellipse 80% 60% at 50% 0%, rgba(${rgb},${dark ? 0.12 : 0.08}) 0%, rgba(${rgb},0) 62%), ${t.bg}`,
    pageBg2: "none", bodyBg: t.bg, cardBg: t.bgCard, cardBg2: t.bgCard,
    cardShadow: dark ? "0 24px 64px rgba(0,0,0,0.55)" : "0 24px 64px rgba(20,24,40,0.12)",
    cardShadow2: dark ? "0 24px 64px rgba(0,0,0,0.55)" : "0 24px 64px rgba(20,24,40,0.12)",
    inputBg: t.bgInner, chipBg: t.bgInner, tickOff: t.border,
    btnBg: t.ac,
    fontDisplay: t.fontDisplay, fontBody: t.fontBody, fontLayout: t.fontBody, fontInter: t.fontBody,
  };
}

export function AuthBackground() {
  const P = authPalette();
  const canvasRef = React.useRef(null);
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let W = canvas.width = window.innerWidth;
    let H = canvas.height = window.innerHeight;
    const onResize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
    window.addEventListener("resize", onResize);

    // Mouse tracking (suavizado com lerp)
    const mouse = { x: W / 2, y: H / 2 };
    const smoothMouse = { x: W / 2, y: H / 2 };
    const onMouseMove = (e) => { mouse.x = e.clientX; mouse.y = e.clientY; };
    window.addEventListener("mousemove", onMouseMove);

    // Grid dots
    const COLS = 28, ROWS = 18;
    const MOUSE_RADIUS = 120;   // raio de influência do mouse
    const MOUSE_STRENGTH = 22;  // intensidade da atração
    const dots = [];
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        dots.push({
          bx: (c / (COLS - 1)) * W,
          by: (r / (ROWS - 1)) * H,
          x: 0, y: 0,
          phase: Math.random() * Math.PI * 2,
          amp: 6 + Math.random() * 14,
          spd: 0.3 + Math.random() * 0.5,
        });
      }
    }

    let t = 0, raf;
    function draw() {
      ctx.clearRect(0, 0, W, H);
      t += 0.012;

      // Lerp smooth mouse — segue com inércia suave
      smoothMouse.x += (mouse.x - smoothMouse.x) * 0.05;
      smoothMouse.y += (mouse.y - smoothMouse.y) * 0.05;

      // Update positions with mouse attraction
      dots.forEach(d => {
        const baseX = d.bx + Math.sin(t * d.spd + d.phase) * d.amp;
        const baseY = d.by + Math.cos(t * d.spd * 0.7 + d.phase) * d.amp * 0.6;

        const mdx = smoothMouse.x - baseX;
        const mdy = smoothMouse.y - baseY;
        const mDist = Math.sqrt(mdx * mdx + mdy * mdy);

        if (mDist < MOUSE_RADIUS && mDist > 0) {
          const pull = (1 - mDist / MOUSE_RADIUS) * MOUSE_STRENGTH;
          d.x = baseX + (mdx / mDist) * pull;
          d.y = baseY + (mdy / mDist) * pull;
        } else {
          d.x = baseX;
          d.y = baseY;
        }
      });

      // Draw connections
      for (let i = 0; i < dots.length; i++) {
        for (let j = i + 1; j < dots.length; j++) {
          const dx = dots[i].x - dots[j].x;
          const dy = dots[i].y - dots[j].y;
          const dist = Math.sqrt(dx*dx + dy*dy);
          const maxDist = W / (COLS * 0.8);
          if (dist < maxDist) {
            const midX = (dots[i].x + dots[j].x) / 2;
            const midY = (dots[i].y + dots[j].y) / 2;
            const toMouse = Math.sqrt((midX - smoothMouse.x) ** 2 + (midY - smoothMouse.y) ** 2);
            const mouseBright = toMouse < MOUSE_RADIUS ? (1 - toMouse / MOUSE_RADIUS) * 0.12 : 0;
            const alpha = (1 - dist / maxDist) * 0.10 + mouseBright;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(${P.rgb},${Math.min(alpha, 0.28)})`;
            ctx.lineWidth = 0.4;
            ctx.moveTo(dots[i].x, dots[i].y);
            ctx.lineTo(dots[j].x, dots[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw dots
      dots.forEach(d => {
        const toMouse = Math.sqrt((d.x - smoothMouse.x) ** 2 + (d.y - smoothMouse.y) ** 2);
        const nearMouse = toMouse < MOUSE_RADIUS ? (1 - toMouse / MOUSE_RADIUS) : 0;
        const radius = 1.2 + nearMouse * 1.2;
        const alpha = 0.18 + nearMouse * 0.22;
        ctx.beginPath();
        ctx.arc(d.x, d.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${P.rgb},${alpha})`;
        ctx.fill();
      });

      raf = requestAnimationFrame(draw);
    }
    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMouseMove);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependências mantidas como estavam (mudar alteraria quando o efeito roda)
  }, []);
  return <canvas ref={canvasRef} style={{ position:"fixed", inset:0, width:"100%", height:"100%", pointerEvents:"none" }} />;
}
